import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { testrunService } from "../../services/testrunService";
import { testPlanService } from "../../services/testPlanService";
import toast from "react-hot-toast";
import TableSkeleton from "../skeletons/TableSkeleton";

const LinkTestRunModal = ({
  open,
  onClose,
  projectId,
  planId,
  linkedRunIds = [],
  onLinked,
}) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  useEffect(() => {
    if (open && projectId) {
      setSelectedRunId(null);
      setLoading(true);
      testrunService
        .listTestRuns(projectId, {})
        .then((res) => {
          const list = res?.data?.test_runs ?? res?.test_runs ?? [];
          setRuns(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          console.error(err);
          toast.error(
            err.response?.data?.message || "Failed to load test runs",
          );
          setRuns([]);
        })
        .finally(() => setLoading(false));
    }
  }, [open, projectId]);

  const linkedSet = new Set(Array.isArray(linkedRunIds) ? linkedRunIds : []);
  const availableRuns = runs.filter((r) => !linkedSet.has(r.test_run_id));

  const handleLink = async () => {
    if (!selectedRunId || !planId || !projectId) {
      toast.error("Please select a test run");
      return;
    }
    setLinking(true);
    try {
      await testPlanService.linkRun(planId, projectId, selectedRunId);
      toast.success("Test run linked to plan");
      onLinked?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to link test run");
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Link existing test run</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 mb-4">
          Select a test run from this project to link to the test plan.
        </p>
        {loading ? (
          <div className="min-h-[120px] p-2">
            <TableSkeleton rows={4} columns={2} />
          </div>
        ) : availableRuns.length === 0 ? (
          <div className="min-h-[120px] flex items-center justify-center text-gray-500 text-sm">
            {runs.length === 0
              ? "No test runs in this project."
              : "All project test runs are already linked to this plan."}
          </div>
        ) : (
          <div className="max-h-[280px] overflow-y-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3 font-medium text-gray-700 w-8" />
                  <th className="text-left py-2 px-3 font-medium text-gray-700">
                    Name
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">
                    Assigned
                  </th>
                </tr>
              </thead>
              <tbody>
                {availableRuns.map((run) => (
                  <tr
                    key={run.test_run_id}
                    className={`border-t cursor-pointer hover:bg-gray-50 ${
                      selectedRunId === run.test_run_id ? "bg-indigo-50" : ""
                    }`}
                    onClick={() => setSelectedRunId(run.test_run_id)}
                  >
                    <td className="py-2 px-3">
                      <input
                        type="radio"
                        name="linkRun"
                        checked={selectedRunId === run.test_run_id}
                        onChange={() => setSelectedRunId(run.test_run_id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="py-2 px-3 font-medium text-gray-900">
                      {run.name || run.test_run_id}
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {run.status || "–"}
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {run.assigned_to_name || run.assigned_to_email || "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleLink}
            disabled={!selectedRunId || linking || availableRuns.length === 0}
          >
            {linking ? "Linking..." : "Link run"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkTestRunModal;
